import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import districtData from '../../../assets/jsons/district.json';
import {
  getFilteredMonths,
  mapCategoryOptions,
  mapManagementOptions,
  mapDesignationOptions,
  AdminDrpDwnsResult,
} from './common.methods';
import {
  usePpGetAdminDrpDwnsQuery,
  useGetBlockListQuery,
} from '../Pallipaarvai.service';

interface PPDashboardFilterProps {
  filters: FilterState;
  onFilter: (filters: FilterState) => void;
}

export interface FilterState {
  academicYear: string | null;
  month_id: string | null;
  district: string | null;
  block: string | null;
  category: string | null;
  management: string | null;
  designation: string | null;
}

const PPDashboardFilter: React.FC<PPDashboardFilterProps> = ({
  filters,
  onFilter,
}) => {
  // RTK Query — fetch admin dropdown data
  const { data: adminDrpDwnsRes } = usePpGetAdminDrpDwnsQuery();
  const apiResult = adminDrpDwnsRes?.records as AdminDrpDwnsResult | undefined;

  // RTK Query — fetch block list from CloudFront
  const { data: blockListData } = useGetBlockListQuery();

  // District options from JSON
  const districtOptions = districtData.map((d) => ({
    label: d.district_name,
    value: String(d.id),
  }));

  // Academic Year static data
  const academicYearData = [
    {
      id: '4',
      name: '2025-2026',
      start_date: '2025-06-01',
      end_date: '2026-06-30',
      status: 'in_progress',
      classes_apply: '1,2,3,4,5',
      subjects_apply: '3,46,48,45,94,95,96,7,8',
      sequence_id: '35',
    },
    {
      id: '37',
      name: '2026-2027',
      start_date: '2026-06-01',
      end_date: '2027-06-30',
      status: 'in_progress',
      classes_apply: '1,2,3,4,5',
      subjects_apply: '3,46,48,45,94,95,96,7,8',
      sequence_id: '35',
    },
  ];

  const academicYearOptions = academicYearData.map((ay) => ({
    label: ay.name,
    value: ay.id,
  }));

  // Get selected academic year name for month filtering
  const selectedAcademicYear = academicYearData.find(
    (ay) => ay.id === filters.academicYear
  );
  const monthOptions = selectedAcademicYear
    ? getFilteredMonths(selectedAcademicYear.name)
    : [];

  // Map API response to dropdown options
  const categoryOptions = apiResult?.cateList
    ? mapCategoryOptions(apiResult.cateList)
    : [];
  const managementOptions = apiResult?.managementList
    ? mapManagementOptions(apiResult.managementList)
    : [];
  const designationOptions = apiResult?.veracityUtypes
    ? mapDesignationOptions(apiResult.veracityUtypes)
    : [];

  // Block options — filtered by selected district
  const blockOptions = filters.district
    ? (blockListData ?? [])
        .filter((b) => b.distId === parseInt(filters.district!, 10))
        .map((b) => ({ label: b.blkName, value: String(b.blkId) }))
    : [];

  // Helper to emit updated filters
  const update = (partial: Partial<FilterState>) => {
    onFilter({ ...filters, ...partial });
  };

  // Reset all filters except academic year and month
  const handleReset = () => {
    update({
      district: null,
      block: null,
      category: null,
      management: null,
      designation: null,
    });
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    letterSpacing: '0.5px',
  };

  return (
    <div
      className="d-flex align-items-end flex-nowrap gap-2 p-3 rounded-3 border"
      style={{
        background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
      }}
    >
      <div className="flex-fill" style={{ minWidth: '120px' }}>
        <label
          className="form-label mb-1 fw-semibold text-uppercase text-secondary"
          style={labelStyle}
        >
          Academic Year
        </label>
        <Dropdown
          value={filters.academicYear}
          options={academicYearOptions}
          onChange={(e) => update({ academicYear: e.value })}
          placeholder="Select Year"
          className="w-100 p-inputtext-sm"
          filter
        />
      </div>

      <div className="flex-fill" style={{ minWidth: '110px' }}>
        <label
          className="form-label mb-1 fw-semibold text-uppercase text-secondary"
          style={labelStyle}
        >
          Month
        </label>
        <Dropdown
          value={filters.month_id}
          options={monthOptions}
          onChange={(e) => update({ month_id: e.value })}
          placeholder="Select Month"
          className="w-100 p-inputtext-sm"
          filter
        />
      </div>

      <div className="flex-fill" style={{ minWidth: '120px' }}>
        <label
          className="form-label mb-1 fw-semibold text-uppercase text-secondary"
          style={labelStyle}
        >
          District
        </label>
        <Dropdown
          value={filters.district}
          options={districtOptions}
          onChange={(e) => update({ district: e.value, block: null })}
          placeholder="All Districts"
          className="w-100 p-inputtext-sm"
          showClear
          filter
        />
      </div>

      <div className="flex-fill" style={{ minWidth: '110px' }}>
        <label
          className="form-label mb-1 fw-semibold text-uppercase text-secondary"
          style={labelStyle}
        >
          Block
        </label>
        <Dropdown
          value={filters.block}
          options={blockOptions}
          onChange={(e) => update({ block: e.value })}
          placeholder="All Blocks"
          className="w-100 p-inputtext-sm"
          showClear
          filter
        />
      </div>

      <div className="flex-fill" style={{ minWidth: '120px' }}>
        <label
          className="form-label mb-1 fw-semibold text-uppercase text-secondary"
          style={labelStyle}
        >
          Category
        </label>
        <Dropdown
          value={filters.category}
          options={categoryOptions}
          onChange={(e) => update({ category: e.value })}
          placeholder="All Categories"
          className="w-100 p-inputtext-sm"
          showClear
          filter
        />
      </div>

      <div className="flex-fill" style={{ minWidth: '130px' }}>
        <label
          className="form-label mb-1 fw-semibold text-uppercase text-secondary"
          style={labelStyle}
        >
          Management
        </label>
        <Dropdown
          value={filters.management}
          options={managementOptions}
          onChange={(e) => update({ management: e.value })}
          placeholder="All Management"
          className="w-100 p-inputtext-sm"
          showClear
          filter
        />
      </div>

      <div className="flex-fill" style={{ minWidth: '130px' }}>
        <label
          className="form-label mb-1 fw-semibold text-uppercase text-secondary"
          style={labelStyle}
        >
          Designation
        </label>
        <Dropdown
          value={filters.designation}
          options={designationOptions}
          onChange={(e) => update({ designation: e.value })}
          placeholder="All Designations"
          className="w-100 p-inputtext-sm"
          showClear
          filter
        />
      </div>

      <div>
        <Button
          label="Reset"
          icon="pi pi-refresh"
          className="p-button-sm"
          onClick={handleReset}
          style={{
            backgroundColor: '#1e293b',
            border: 'none',
            borderRadius: '8px',
          }}
        />
      </div>
    </div>
  );
};

export default PPDashboardFilter;
